import { makeAutoObservable, runInAction } from 'mobx';

export interface CalibrationMethod {
  id: string;
  name: string;
  description: string;
  realWorldSize: { width: number; height: number }; // in mm
}

export interface Measurement {
  id: string;
  name: string;
  pixelLength: number;
  realLength: number;
  unit: string;
  timestamp: number;
}

export class RulerStore {
  private pixelsPerMM: number = 3.779; // Default assumption for 96 DPI
  private isCalibrated: boolean = false;
  private calibrationMethod: string = '';
  private measurements: Measurement[] = [];
  private selectedUnit: string = 'mm';

  constructor() {
    makeAutoObservable(this);
    this.loadCalibration();
  }

  // Calibration methods
  get availableCalibrationMethods(): CalibrationMethod[] {
    return [
      {
        id: 'credit-card',
        name: 'Credit Card',
        description: 'Use a standard credit card for calibration',
        realWorldSize: { width: 85.6, height: 53.98 }
      },
      {
        id: 'business-card',
        name: 'Business Card',
        description: 'Use a standard business card',
        realWorldSize: { width: 89, height: 51 }
      },
      {
        id: 'us-quarter',
        name: 'US Quarter',
        description: 'Use a US quarter coin',
        realWorldSize: { width: 24.26, height: 24.26 }
      },
      {
        id: 'us-penny',
        name: 'US Penny',
        description: 'Use a US penny coin',
        realWorldSize: { width: 19.05, height: 19.05 }
      },
      {
        id: 'euro-coin',
        name: 'Euro Coin (1€)',
        description: 'Use a 1 Euro coin',
        realWorldSize: { width: 23.25, height: 23.25 }
      },
      {
        id: 'custom',
        name: 'Custom Object',
        description: 'Use a custom object with known dimensions',
        realWorldSize: { width: 0, height: 0 }
      }
    ];
  }

  get currentPixelsPerMM(): number {
    return this.pixelsPerMM;
  }

  get currentPixelsPerInch(): number {
    return this.pixelsPerMM * 25.4;
  }

  get isCalibrationSet(): boolean {
    return this.isCalibrated;
  }

  get calibrationMethodUsed(): string {
    return this.calibrationMethod;
  }

  get allMeasurements(): Measurement[] {
    return this.measurements;
  }

  get currentUnit(): string {
    return this.selectedUnit;
  }

  get availableUnits(): string[] {
    return ['mm', 'cm', 'inch', 'px'];
  }

  setSelectedUnit(unit: string): void {
    runInAction(() => {
      this.selectedUnit = unit;
    });
  }

  calibrate(method: CalibrationMethod, measuredPixels: number, customSize?: { width: number; height: number }): void {
    const realSize = method.id === 'custom' && customSize ? customSize.width : method.realWorldSize.width;
    
    if (realSize <= 0 || measuredPixels <= 0) {
      throw new Error('Invalid calibration values');
    }

    runInAction(() => {
      this.pixelsPerMM = measuredPixels / realSize;
      this.isCalibrated = true;
      this.calibrationMethod = method.name;
    });

    this.saveCalibration();
  }

  addMeasurement(name: string, pixelLength: number): void {
    const realLength = this.convertPixelsToUnit(pixelLength, this.selectedUnit);
    
    const measurement: Measurement = {
      id: Date.now().toString(),
      name,
      pixelLength,
      realLength,
      unit: this.selectedUnit,
      timestamp: Date.now()
    };

    runInAction(() => {
      this.measurements.push(measurement);
    });

    this.saveMeasurements();
  }

  deleteMeasurement(id: string): void {
    runInAction(() => {
      this.measurements = this.measurements.filter(m => m.id !== id);
    });

    this.saveMeasurements();
  }

  clearAllMeasurements(): void {
    runInAction(() => {
      this.measurements = [];
    });

    this.saveMeasurements();
  }

  convertPixelsToUnit(pixels: number, unit: string): number {
    switch (unit) {
      case 'mm':
        return pixels / this.pixelsPerMM;
      case 'cm':
        return pixels / (this.pixelsPerMM * 10);
      case 'inch':
        return pixels / (this.pixelsPerMM * 25.4);
      case 'px':
        return pixels;
      default:
        return pixels / this.pixelsPerMM;
    }
  }

  formatMeasurement(value: number, unit: string): string {
    const precision = unit === 'px' ? 0 : 2;
    return `${value.toFixed(precision)} ${unit}`;
  }

  resetCalibration(): void {
    runInAction(() => {
      this.pixelsPerMM = 3.779; // Reset to default
      this.isCalibrated = false;
      this.calibrationMethod = '';
    });

    this.saveCalibration();
  }

  // Screen info utilities
  getScreenInfo(): { width: number; height: number; diagonal: number; estimatedPPI: number } {
    const width = window.screen.width;
    const height = window.screen.height;
    const diagonal = Math.sqrt(width * width + height * height);
    const estimatedPPI = this.currentPixelsPerInch;

    return {
      width,
      height,
      diagonal,
      estimatedPPI
    };
  }

  // Persistence
  private saveCalibration(): void {
    const calibrationData = {
      pixelsPerMM: this.pixelsPerMM,
      isCalibrated: this.isCalibrated,
      calibrationMethod: this.calibrationMethod
    };

    localStorage.setItem('ruler-calibration', JSON.stringify(calibrationData));
  }

  private loadCalibration(): void {
    try {
      const saved = localStorage.getItem('ruler-calibration');
      if (saved) {
        const calibrationData = JSON.parse(saved);
        runInAction(() => {
          this.pixelsPerMM = calibrationData.pixelsPerMM || 3.779;
          this.isCalibrated = calibrationData.isCalibrated || false;
          this.calibrationMethod = calibrationData.calibrationMethod || '';
        });
      }
    } catch (error) {
      console.warn('Failed to load calibration data:', error);
    }

    this.loadMeasurements();
  }

  private saveMeasurements(): void {
    localStorage.setItem('ruler-measurements', JSON.stringify(this.measurements));
  }

  private loadMeasurements(): void {
    try {
      const saved = localStorage.getItem('ruler-measurements');
      if (saved) {
        const measurements = JSON.parse(saved);
        runInAction(() => {
          this.measurements = measurements || [];
        });
      }
    } catch (error) {
      console.warn('Failed to load measurements:', error);
    }
  }
}

export const rulerStore = new RulerStore();
